import React, { useEffect, useRef, useState } from 'react'

interface TooltipProps {
  content: React.ReactNode
  children: React.ReactElement
  delay?: number
  position?: 'top' | 'bottom' | 'left' | 'right'
  theme?: 'dark' | 'light'
  /**
   * Fixed CSS width (e.g. '18rem') for long content. Without this the box
   * shrink-to-fits, capped at max-w-xs — fine for a short label, but when
   * anchored to a small icon it can wrap into an unreadably narrow column
   * instead of actually using that cap (the shrink-to-fit calculation goes
   * off the tiny icon-sized containing block, not the max-width). Leave
   * unset for normal short tooltips; only long ones need this.
   */
  width?: string
}

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  delay = 100,
  position = 'top',
  theme = 'dark',
  width,
}) => {
  const [show, setShow] = useState(false)
  // A ref, not a plain local — a bare `let timeout` is a NEW variable every
  // render, so a re-render landing between showTip's setTimeout firing and
  // the matching hideTip (e.g. from clicking the element this tooltip wraps,
  // which usually triggers a state update elsewhere) leaves hideTip clearing
  // a timeout that already fired, while a genuinely still-pending one from a
  // stale closure keeps running and calls setShow(true) AFTER hideTip ran —
  // showing the tooltip with no cursor left to ever trigger the mouseleave
  // that would hide it again. A ref is the same value across every render,
  // so hideTip always clears whatever showTip most recently scheduled.
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showTip = () => {
    timeoutRef.current = setTimeout(() => {
      setShow(true)
    }, delay)
  }

  const hideTip = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    setShow(false)
  }

  // Safety net for the same race from the other direction — if this
  // component unmounts (or the element it wraps disappears, e.g. closing a
  // dockview panel while its close button is mid-hover) while a showTip
  // timeout is still pending, cancel it rather than calling setShow on an
  // unmounted component.
  useEffect(() => () => { if (timeoutRef.current) clearTimeout(timeoutRef.current) }, [])

  // top-full/bottom-full + margin (not a fixed -top-8/-bottom-8 offset) so
  // this holds up for multi-line content, not just a single line of text.
  const positionClasses = {
    top: 'bottom-full mb-2 left-1/2 transform -translate-x-1/2',
    bottom: 'top-full mt-2 left-1/2 transform -translate-x-1/2',
    left: 'right-full mr-2 top-1/2 transform -translate-y-1/2',
    right: 'left-full ml-2 top-1/2 transform -translate-y-1/2'
  }

  const containerTheme = theme === 'light'
    ? 'text-gray-800 bg-gray-50 border border-gray-200'
    : 'text-white bg-gray-900'

  const arrowBg = theme === 'light' ? 'bg-gray-50 border border-gray-200' : 'bg-gray-900'

  const arrowClasses = {
    top: `w-2 h-2 ${arrowBg} transform rotate-45 -bottom-1 left-1/2 -translate-x-1/2`,
    bottom: `w-2 h-2 ${arrowBg} transform rotate-45 -top-1 left-1/2 -translate-x-1/2`,
    left: `w-2 h-2 ${arrowBg} transform rotate-45 -right-1 top-1/2 -translate-y-1/2`,
    right: `w-2 h-2 ${arrowBg} transform rotate-45 -left-1 top-1/2 -translate-y-1/2`
  }

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={showTip}
        onMouseLeave={hideTip}
        // Clicking whatever this wraps (e.g. a toggle button) usually
        // changes its own state/label right under the cursor without the
        // mouse ever leaving — dismiss immediately rather than leaving a
        // now-stale tooltip label floating over the new state.
        onClick={hideTip}
      >
        {children}
      </div>
      {show && (
        <div
          className={`absolute z-50 ${width ? '' : 'max-w-xs'} px-3 py-2 text-xs font-medium rounded-md shadow-lg pointer-events-none ${containerTheme} ${positionClasses[position]}`}
          style={width ? { animation: 'fadeIn 0.15s ease-in', width, maxWidth: '80vw' } : { animation: 'fadeIn 0.15s ease-in' }}
        >
          {content}
          <div className={`absolute ${arrowClasses[position]}`}></div>
        </div>
      )}
    </div>
  )
}

export default Tooltip